/**
 * shared/src/geoLabel.ts
 *
 * ETICHETTE GEOGRAFICHE LEGGIBILI (fix "Y1 / T2 / T3", Ott 2026).
 *
 * Problema: le etichette dei waypoint/meeting point venivano costruite dal
 * `display_name` di Nominatim (primo token compreso) e salvate così com'erano.
 * In OpenStreetMap però molti elementi hanno NOMI-CODICE: T2/T3/T6…T11 (strade
 * forestali e designazioni di itinerario), Y1 (posteggi aeroportuali
 * `aeroway=holding_position`, ascensori), oltre a feature che non c'entrano
 * nulla con una strada (ascensori, scalinate, fermate bus, alberi).
 * Risultato: percorsi con tappe chiamate "T2, Saint-Rhémy-en-Bosses" o "Y1".
 *
 * Qui vive l'unica logica di normalizzazione, condivisa da client e server:
 *   1. scarta i NOMI-CODICE (T2, Y1, T11, "12") quando esiste un'alternativa;
 *   2. scarta i nomi di feature non stradali/irrilevanti;
 *   3. preferisce l'indirizzo STRUTTURATO (`address.road`, poi località);
 *   4. produce `label` + `detail` (riga secondaria) e conserva il nome grezzo
 *      in `rawName` per audit/diagnostica — così è tracciabile da dove arriva.
 *
 * Nessuna dipendenza, nessun I/O: è pura e testabile.
 */
/** Lunghezza massima dell'etichetta salvata (allineata a route_pois.label 200). */
export const MAX_GEO_LABEL_LENGTH = 120;
/** Nome usato quando non c'è nessuna informazione utile. */
export const UNNAMED_POINT_LABEL = "Punto senza nome";
/**
 * Codici "stile mappa" che non dicono nulla a un motociclista: T2, Y1, T11,
 * W3, "12"… Le sigle stradali italiane (A4, SS225, SP32, E45) NON sono codici
 * in questo senso: sono riferimenti utili e vengono mantenuti.
 */
const ROAD_REF_SIGLA = /^(A|E|SP|SS|SR|SN|NSA|SPB|SPEX|TSP)\s*\d{1,4}$/i;
const CODE_LIKE = /^[A-Za-z]{1,3}[\s.-]?\d{1,3}[A-Za-z]?$/;
const BARE_NUMBER = /^\d{1,4}$/;
/**
 * Classi OSM (infrastruttura/oggetti tecnici) la cui "name" non identifica un
 * luogo per un waypoint di percorso. Volutamente ristretto: NON vengono
 * scartati `natural` (Lago di Como, Monte Bianco), `railway` (stazione),
 * `building`/`office` (Hotel Bellavista) — sono nomi utili per un motociclista.
 */
const IRRELEVANT_CLASSES = new Set([
    "man_made",
    "power",
    "telecom",
    "barrier",
    "indoor",
]);
/**
 * Tipi puntuali (dentro classi altrimenti utili) da non usare come nome:
 * elementi di arredo/segnaletica, ascensori, piazzole aeroportuali (Y1!).
 */
const IRRELEVANT_TYPES = new Set([
    "elevator",
    "steps",
    "bus_stop",
    "platform",
    "rail",
    "signal",
    "switch",
    "buffer_stop",
    "level_crossing",
    "parking_entrance",
    "entrance",
    "traffic_signals",
    "crossing",
    "street_lamp",
    "speed_camera",
    "holding_position",
    "taxiway",
    "runway",
    "apron",
    "gate",
    "tree",
    "survey_point",
]);
/** true se la stringa è un codice ("T2", "Y1", "12") e NON una sigla stradale. */
export function isCodedPlaceName(value) {
    const v = String(value ?? "").trim();
    if (!v)
        return false;
    if (ROAD_REF_SIGLA.test(v))
        return false;
    return CODE_LIKE.test(v) || BARE_NUMBER.test(v);
}
/** true se il valore è una sigla stradale utile (A4, SS225, SP32, E45). */
export function isRoadRef(value) {
    return ROAD_REF_SIGLA.test(String(value ?? "").trim());
}
function isIrrelevantFeature(featureClass, featureType) {
    const cls = String(featureClass ?? "").trim().toLowerCase();
    const type = String(featureType ?? "").trim().toLowerCase();
    if (type && IRRELEVANT_TYPES.has(type))
        return true;
    if (cls && IRRELEVANT_CLASSES.has(cls))
        return true;
    return false;
}
/** Prima parte del display_name ("Via Roma, Milano, Italia" → "Via Roma"). */
function firstDisplayToken(displayName) {
    return String(displayName ?? "").split(",")[0]?.trim() ?? "";
}
/** Le restanti parti del display_name, ripulite. */
function displayTokens(displayName) {
    return String(displayName ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}
function localityFromAddress(address) {
    if (!address)
        return null;
    const candidates = [
        address.village,
        address.hamlet,
        address.town,
        address.city,
        address.municipality,
        address.suburb,
    ];
    for (const c of candidates) {
        const v = String(c ?? "").trim();
        if (v)
            return v;
    }
    return null;
}
function roadFromAddress(address) {
    if (!address)
        return null;
    const candidates = [
        address.road,
        address.pedestrian,
        address.path,
        address.footway,
        address.track,
    ];
    for (const c of candidates) {
        const v = String(c ?? "").trim();
        if (v)
            return v;
    }
    return null;
}
function truncate(value, max = MAX_GEO_LABEL_LENGTH) {
    const v = value.trim();
    return v.length <= max ? v : `${v.slice(0, max - 1).trimEnd()}…`;
}
/**
 * Costruisce l'etichetta leggibile da una risposta di geocoding inverso.
 *
 * Ordine di preferenza (dal più informativo al meno):
 *   1. nome scelto dall'utente (`placeName`) se non è un codice;
 *   2. strada da indirizzo strutturato;
 *   3. nome della feature dal provider (se non codice e non irrilevante);
 *   4. località (comune/frazione);
 *   5. sigla stradale presente nel nome;
 *   6. `UNNAMED_POINT_LABEL`.
 */
export function buildGeoLabel(input) {
    const rawName = String(input.placeName ?? input.displayName ?? "").trim() || null;
    const first = firstDisplayToken(input.displayName) || null;
    const tokens = displayTokens(input.displayName);
    const locality = localityFromAddress(input.address);
    const road = roadFromAddress(input.address);
    const irrelevant = isIrrelevantFeature(input.featureClass, input.featureType);
    const codeLikeFirst = isCodedPlaceName(first);
    const wasCodeLike = codeLikeFirst || isCodedPlaceName(input.placeName ?? null);
    const usable = (candidate) => {
        const v = String(candidate ?? "").trim();
        if (!v)
            return null;
        if (isCodedPlaceName(v) && !isRoadRef(v))
            return null;
        return v;
    };
    let label = null;
    let usedFallback = false;
    // 1. nome scelto dall'utente (autocomplete) — non usato se la feature è
    //    irrilevante (es. "Ascensore Montecitorio" o il codice "Y1")
    if (!irrelevant)
        label = usable(input.placeName);
    // 2. strada strutturata (salta se è la stessa cosa di un codice)
    if (!label)
        label = usable(road);
    // 3. nome della feature dal provider (solo se pertinente)
    if (!label && !irrelevant)
        label = usable(first);
    // 4. località
    if (!label)
        label = usable(locality);
    // 4b. località dedotta dal display_name (secondo token)
    if (!label)
        label = usable(tokens.find((t, i) => i > 0 && !isCodedPlaceName(t)));
    // 5. sigla stradale (A4/SS225/SP32) — informativa anche da sola
    if (!label)
        label = usable(first) ?? (isRoadRef(first) ? first : null);
    if (!label) {
        usedFallback = true;
        label = UNNAMED_POINT_LABEL;
    }
    // Dettaglio: contesto amministrativo + località, senza ripetere la label.
    // Preferisce `address` strutturato; i token del display_name sono l'ultima
    // risorsa (evita "Bormio, Lombardia, Italia" quando manca l'address).
    const detailParts = [];
    const pushDetail = (value) => {
        const v = String(value ?? "").trim();
        if (!v)
            return;
        if (v.toLowerCase() === label.toLowerCase())
            return;
        if (detailParts.some((p) => p.toLowerCase() === v.toLowerCase()))
            return;
        detailParts.push(v);
    };
    pushDetail(locality);
    const adminContext = [input.address?.county, input.address?.state].filter((v) => Boolean(v && String(v).trim()));
    for (const part of adminContext)
        pushDetail(part);
    if (detailParts.length === 0 && tokens.length > 1) {
        pushDetail(tokens[1]);
        if (detailParts.length === 0)
            pushDetail(tokens[2]);
    }
    const detail = detailParts.slice(0, 2).join(", ") || null;
    const finalLabel = truncate(label);
    return {
        label: finalLabel,
        detail: detail ? truncate(detail, 80) : null,
        full: detail ? truncate(`${finalLabel}, ${detail}`) : finalLabel,
        wasCodeLike,
        droppedIrrelevantFeature: irrelevant,
        usedFallback,
        rawName,
    };
}
/**
 * Etichetta "di percorso": aggiunge il progressivo chilometrico quando
 * disponibile, così una tappa senza nome resta comunque utile ("Km 12,3").
 */
export function formatRoutePointLabel(result, km) {
    const base = result.full;
    if (km === null || km === undefined || !Number.isFinite(km))
        return base;
    const kmText = `Km ${km.toFixed(1).replace(".", ",")}`;
    if (result.usedFallback)
        return `${kmText} · ${UNNAMED_POINT_LABEL}`;
    if (result.label === kmText)
        return base;
    return `${kmText} · ${base}`;
}
//# sourceMappingURL=geoLabel.js.map